import React, { useState, useEffect } from 'react'
import { PageHeader } from '@/components/layout'
import { useCourses } from '@/hooks/useCourses'
import { useAuth } from '@/hooks/useAuth'
import { useAffiliateByUserID } from '@/hooks/useAffiliate'
import { affiliateService } from '@/services/affiliateService'
import type { AffiliateCourseCommission } from '@/types/affiliate'

export const AffiliateLinksPage: React.FC = () => {
    const { user } = useAuth()
    const { data: courses, isLoading: coursesLoading } = useCourses()
    const { data: affiliate, isLoading: affiliateLoading } = useAffiliateByUserID(user?.id || '')
    const [copiedId, setCopiedId] = useState<string | null>(null)
    const [courseCommissions, setCourseCommissions] = useState<AffiliateCourseCommission[]>([])

    const isLoading = coursesLoading || affiliateLoading
    const affiliateCode = affiliate?.code || ''

    // Fetch per-course commissions
    useEffect(() => {
        if (affiliate?.id) {
            affiliateService.getAffiliateCourseCommissions(affiliate.id)
                .then(setCourseCommissions)
                .catch(console.error)
        }
    }, [affiliate?.id])

    const copyToClipboard = (courseId: string, link: string) => {
        navigator.clipboard.writeText(link)
        setCopiedId(courseId)
        setTimeout(() => setCopiedId(null), 2000)
    }

    return (
        <div>
            <PageHeader
                title="My Affiliate Links"
                subtitle="Generate and manage your referral links"
            />

            {/* Affiliate Code */}
            <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5 mb-6">
                {isLoading ? (
                    <div className="animate-pulse">
                        <div className="h-4 bg-gray-100 dark:bg-white/10 rounded mb-2 w-32" />
                        <div className="h-8 bg-gray-100 dark:bg-white/10 rounded w-48" />
                    </div>
                ) : affiliate ? (
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-500 dark:text-text-muted uppercase font-medium mb-1">Your Affiliate Code</p>
                            <p className="text-2xl font-bold text-red-400">{affiliateCode}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right text-sm text-gray-500 dark:text-text-muted">
                                <p>Commission Rate</p>
                                <p className="text-gray-900 dark:text-white font-bold">
                                    {affiliate.commissionType === 'percentage'
                                        ? `${affiliate.commissionValue}% per sale`
                                        : `Rp ${affiliate.commissionValue?.toLocaleString('id-ID')} per sale`}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-4 text-gray-500 dark:text-text-muted">
                        <p className="text-sm">No affiliate account found</p>
                        <p className="text-xs mt-1">Contact admin to create one</p>
                    </div>
                )}
            </div>

            {/* Course Links */}
            <div className="space-y-4">
                {isLoading ? (
                    <>
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-32 rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 animate-pulse" />
                        ))}
                    </>
                ) : !affiliate ? (
                    <div className="text-center py-12 text-gray-500 dark:text-text-muted">
                        <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
                            <span className="material-symbols-outlined text-[32px] text-gray-500">link</span>
                        </div>
                        <p className="text-gray-900 dark:text-white font-bold mb-2">No affiliate account</p>
                        <p className="text-sm">Contact admin to create an affiliate account</p>
                    </div>
                ) : courses && courses.length > 0 ? (
                    courses.map((course: any) => {
                        const baseUrl = window.location.origin
                        const affiliateLink = `${baseUrl}/course/${course.slug}?ref=${affiliateCode}`
                        const isCopied = copiedId === course.id

                        // Calculate commission with per-course override
                        // Priority: Per-course commission > Global commission
                        const courseCommission = courseCommissions.find(cc => cc.courseId === course.id)
                        const commissionType = courseCommission?.commissionType || affiliate.commissionType
                        const commissionValue = courseCommission?.commissionValue || affiliate.commissionValue

                        let commission = 0
                        if (commissionType === 'percentage') {
                            commission = (course.price || 0) * (commissionValue / 100)
                        } else {
                            commission = commissionValue
                        }

                        const isCustomCommission = !!courseCommission

                        return (
                            <div key={course.id} className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5 hover:border-primary/20 transition-colors">
                                <div className="flex items-start gap-4">
                                    <div
                                        className="size-16 rounded-lg bg-cover bg-center bg-gray-700 shrink-0"
                                        style={{ backgroundImage: course.thumbnailUrl ? `url(${course.thumbnailUrl})` : undefined }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">{course.title}</h3>
                                        <p className="text-xs text-gray-500 dark:text-text-muted mb-3">
                                            Price: Rp {course.price?.toLocaleString('id-ID') || '0'} •
                                            Your commission: <span className="text-red-400 font-bold">Rp {commission.toLocaleString('id-ID')}</span>
                                            {isCustomCommission && <span className="ml-1 px-2 py-0.5 bg-primary/20 text-red-400 rounded text-[10px] font-bold">CUSTOM</span>}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 px-3 py-2 bg-gray-100 dark:bg-white/5 rounded-lg text-xs text-gray-500 dark:text-text-muted truncate">
                                                {affiliateLink}
                                            </div>
                                            <button
                                                onClick={() => copyToClipboard(course.id, affiliateLink)}
                                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${isCopied
                                                        ? 'bg-green-500/20 text-green-400'
                                                        : 'bg-primary text-white hover:scale-105'
                                                    }`}
                                            >
                                                {isCopied ? 'Copied!' : 'Copy Link'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })
                ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-text-muted">
                        <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
                            <span className="material-symbols-outlined text-[32px] text-gray-500">link</span>
                        </div>
                        <p className="text-gray-900 dark:text-white font-bold mb-2">No courses available</p>
                        <p className="text-sm">Check back later for courses to promote.</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AffiliateLinksPage
